/**
 * Social graph service - fetching followers and following lists from Bluesky API
 */
import { Agent, AtpAgent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { graphqlQuery } from '@/services/graphqlService'
import { isBridgedFromAP, extractHandleFromBridgyUrl, cleanBridgyFedHandle } from '@/lib/bridge'

export interface SocialGraphProfile {
    did: string
    handle: string
    displayName?: string
    avatar?: string
    description?: string
    isVerified: boolean
    /** URI of the follow record if the authenticated user follows this profile */
    followingUri?: string
    /** Whether this account is bridged from ActivityPub via Bridgy Fed */
    isBridgedFromAP?: boolean
    /** Whether this account is bridged from AT Protocol via Bridgy Fed */
    isBridgedFromAT?: boolean
    /**
     * For AT-bridged accounts in AP lists: the canonical `X@bsky.brid.gy` handle from
     * `deriveATBridgeFromAcct`. Never a DID parsed from remote-instance data — see #2226.
     */
    bridgedHandle?: string
}

export interface SocialGraphPage {
    profiles: SocialGraphProfile[]
    cursor?: string
}

/** Resolved bridge source data for a single profile */
export interface BridgeSourceData {
    handle: string
    description?: string
}

export function mapProfile(profile: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
    description?: string
    verification?: { verifiedStatus?: string }
    viewer?: { following?: string }
    labels?: { val: string }[]
}): SocialGraphProfile {
    return {
        did: profile.did,
        handle: profile.handle,
        displayName: profile.displayName,
        avatar: profile.avatar,
        description: profile.description,
        isVerified: profile.verification?.verifiedStatus === 'valid',
        followingUri: profile.viewer?.following,
        isBridgedFromAP: isBridgedFromAP(profile.labels),
    }
}

// Lightweight GraphQL query to fetch only bridge status
const BRIDGE_STATUS_QUERY = `
query GetBridgeStatus($identifier: String!) {
  user(identifier: $identifier) {
    did
    bridgeStatus { isBridged activityPubUrl }
  }
}
`

// Fetch source AP profile description
const AP_PROFILE_QUERY = `
query GetAPUser($handle: String!) {
  apUser(handle: $handle) {
    description
  }
}
`

interface BridgeStatusGQLResponse {
    user: {
        did: string
        bridgeStatus?: {
            isBridged: boolean
            activityPubUrl?: string
        }
    } | null
}

interface APProfileGQLResponse {
    apUser: {
        description?: string
    } | null
}

/**
 * Resolve a single bridged profile: get bridge status, extract source handle,
 * then fetch the source AP profile description.
 */
async function resolveSingleBridgeSource(
    did: string
): Promise<{ handle: string; description?: string } | null> {
    const bridgeRes = await graphqlQuery<BridgeStatusGQLResponse>(BRIDGE_STATUS_QUERY, {
        identifier: did,
    })

    const bridgeStatus = bridgeRes.data?.user?.bridgeStatus
    if (!bridgeStatus?.isBridged || !bridgeStatus.activityPubUrl) return null

    const sourceHandle = extractHandleFromBridgyUrl(bridgeStatus.activityPubUrl)
    if (!sourceHandle) return null

    // Fetch the source AP profile for its description
    const apRes = await graphqlQuery<APProfileGQLResponse>(AP_PROFILE_QUERY, {
        handle: sourceHandle,
    })

    return { handle: sourceHandle, description: apRes.data?.apUser?.description }
}

/**
 * Batch-resolve bridge sources for a list of bridged DIDs.
 * Returns a map of DID → source data (handle + description).
 * Runs all resolutions in parallel; individual failures are silently skipped.
 */
export async function resolveBridgeSources(
    bridgedDids: string[]
): Promise<Map<string, BridgeSourceData>> {
    const sourceMap = new Map<string, BridgeSourceData>()
    if (bridgedDids.length === 0) return sourceMap

    const results = await Promise.allSettled(bridgedDids.map(resolveSingleBridgeSource))

    for (let i = 0; i < bridgedDids.length; i++) {
        const result = results[i]
        if (result.status !== 'fulfilled' || !result.value) continue
        sourceMap.set(bridgedDids[i], result.value)
    }

    return sourceMap
}

// Fetch AT Protocol user profile by handle (for AT-bridged accounts in AP lists).
// `user(identifier:)` accepts a handle directly — no DID resolution needed client-side.
const AT_USER_PROFILE_QUERY = `
query GetATUserProfile($identifier: String!) {
  user(identifier: $identifier) {
    did
    repo { handle }
    record(collection: "app.bsky.actor.profile", rkey: "self") {
      value
    }
  }
}
`

interface ATUserProfileGQLResponse {
    user: {
        did: string
        repo?: { handle: string }
        record?: { value?: { description?: string } }
    } | null
}

async function resolveATBridgeSource(atHandle: string): Promise<BridgeSourceData | null> {
    // cleanBridgyFedHandle returns string | null — never send null/'null' as a GraphQL identifier.
    const identifier = cleanBridgyFedHandle(atHandle)
    if (!identifier) return null
    const res = await graphqlQuery<ATUserProfileGQLResponse>(AT_USER_PROFILE_QUERY, {
        identifier,
    })
    const user = res.data?.user
    if (!user?.repo?.handle) return null
    return { handle: user.repo.handle, description: user.record?.value?.description }
}

/**
 * Batch-resolve AT Protocol profiles for bridged-from-AT accounts in AP lists.
 * Takes entries mapping profile IDs to canonical AT-bridge handles, returns a map
 * keyed by profile ID.
 */
export async function resolveATBridgeSources(
    entries: { profileId: string; atHandle: string }[]
): Promise<Map<string, BridgeSourceData>> {
    const sourceMap = new Map<string, BridgeSourceData>()
    if (entries.length === 0) return sourceMap

    const results = await Promise.allSettled(entries.map(e => resolveATBridgeSource(e.atHandle)))

    for (let i = 0; i < entries.length; i++) {
        const result = results[i]
        if (result.status !== 'fulfilled' || !result.value) continue
        sourceMap.set(entries[i].profileId, result.value)
    }

    return sourceMap
}

export async function fetchFollowers(
    identifier: string,
    options: { cursor?: string; limit?: number; sessionAgent?: Agent | null; publicAgent: AtpAgent }
): Promise<SocialGraphPage> {
    const agent = options.sessionAgent ?? options.publicAgent
    const res = await agent.app.bsky.graph.getFollowers({
        actor: identifier,
        limit: options.limit ?? 50,
        cursor: options.cursor,
    })

    return {
        profiles: res.data.followers.map(mapProfile),
        cursor: res.data.cursor,
    }
}

export async function fetchFollowing(
    identifier: string,
    options: { cursor?: string; limit?: number; sessionAgent?: Agent | null; publicAgent: AtpAgent }
): Promise<SocialGraphPage> {
    const agent = options.sessionAgent ?? options.publicAgent
    const res = await agent.app.bsky.graph.getFollows({
        actor: identifier,
        limit: options.limit ?? 50,
        cursor: options.cursor,
    })

    return {
        profiles: res.data.follows.map(mapProfile),
        cursor: res.data.cursor,
    }
}

export async function fetchFollowStatus(
    agent: Agent,
    did: string
): Promise<{ followingUri?: string }> {
    const res = await agent.app.bsky.actor.getProfile({ actor: did })
    return { followingUri: res.data.viewer?.following }
}

export async function fetchBlockStatus(agent: Agent, did: string): Promise<{ blockUri?: string }> {
    const res = await agent.app.bsky.actor.getProfile({ actor: did })
    return { blockUri: res.data.viewer?.blocking }
}

export interface BlockedProfile extends SocialGraphProfile {
    /** The block record URI (at://did/app.bsky.graph.block/rkey) — needed to delete the block */
    blockUri?: string
}

export interface BlockedProfilesPage {
    profiles: BlockedProfile[]
    cursor?: string
}

export async function fetchBlueskyBlocks(
    session: OAuthSession,
    options?: { cursor?: string; limit?: number }
): Promise<BlockedProfilesPage> {
    const agent = new Agent(session)
    const res = await agent.app.bsky.graph.getBlocks({
        limit: options?.limit ?? 50,
        cursor: options?.cursor,
    })

    return {
        profiles: res.data.blocks.map(p => ({
            ...mapProfile(p),
            blockUri: p.viewer?.blocking,
        })),
        cursor: res.data.cursor,
    }
}

export async function followUser(session: OAuthSession, did: string): Promise<string> {
    const agent = new Agent(session)
    const res = await agent.follow(did)
    return res.uri
}

export async function unfollowUser(session: OAuthSession, followUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.deleteFollow(followUri)
}

export async function blockBlueskyUser(session: OAuthSession, did: string): Promise<string> {
    const agent = new Agent(session)
    const response = await agent.com.atproto.repo.createRecord({
        repo: session.did,
        collection: 'app.bsky.graph.block',
        record: {
            $type: 'app.bsky.graph.block',
            subject: did,
            createdAt: new Date().toISOString(),
        },
    })
    return response.data.uri
}

export async function unblockBlueskyUser(session: OAuthSession, blockUri: string): Promise<void> {
    const agent = new Agent(session)
    const uriParts = blockUri.split('/')
    const rkey = uriParts[uriParts.length - 1]
    // AT Protocol rkeys for createRecord-generated records are 13-char base32 TIDs
    if (!rkey || !/^[a-z2-7]{13}$/.test(rkey))
        throw new Error(`Cannot unblock: invalid block URI: ${blockUri}`)
    await agent.com.atproto.repo.deleteRecord({
        repo: session.did,
        collection: 'app.bsky.graph.block',
        rkey,
    })
}
